/**
 * Polygon Slider Store.
 *
 * Shared state between the three `ThreePlumeVisualization` instances on the
 * polygon site page (`bars-only`, `plume-2d`, `plume-3d`). Each instance
 * re-mounts independently, so a local `useState` would let the slider drift
 * out of sync between the legend area and the maps.
 *
 * The slider lives under the MCL legend (bars-only mode renders it), but the
 * 2D + 3D map instances need to read the slider's output to:
 *   - load historical / predictive plume boundaries
 *   - apply v3.2 horizon overrides on top of the boundaries
 *   - swap PSH well data for the chosen date
 *
 * Keeping all of that in zustand lets every instance subscribe to the same
 * slice without prop-drilling through PolygonSiteModeling.
 *
 * Site-scoped: when `siteId` changes the store resets, so navigating
 * between sites doesn't carry stale slider state.
 */
import { create } from 'zustand';
import type { ContaminantBoundaryData } from '../../api/polygon';

interface PshWell {
  well_id: string;
  lat: number;
  lon: number;
  product_thickness_ft: number;
  depth_to_water_ft?: number;
  is_inferred?: boolean;
}

interface PolygonSliderState {
  /** Site the current state belongs to — reset when this changes. */
  siteId: string | null;

  /** v3.2 horizon in days (0 = "today" / historical, 90 / 365 / ... / 7300 for future). */
  selectedHorizonDays: number;
  setSelectedHorizonDays: (days: number) => void;

  /** Boundaries the slider is currently emitting (from snapshot / API). null
   *  = use the live `allContaminantBoundaries` instead. */
  timeSliderBoundaries: ContaminantBoundaryData[] | null;
  setTimeSliderBoundaries: (boundaries: ContaminantBoundaryData[] | null) => void;

  timeSliderFlowDirection: number | null;
  setTimeSliderFlowDirection: (direction: number | null) => void;

  /** ISO date string the slider is parked on. */
  timeSliderDate: string;
  setTimeSliderDate: (date: string) => void;

  /** PSH wells from a quarterly snapshot, when one is available; null falls
   *  back to the live PSH API. */
  snapshotPshWells: PshWell[] | null;
  setSnapshotPshWells: (wells: PshWell[] | null) => void;

  /** Reset for a new site. Called by `ThreePlumeVisualization` on mount. */
  resetForSite: (siteId: string | null) => void;
}

export const usePolygonSliderStore = create<PolygonSliderState>((set, get) => ({
  siteId: null,
  selectedHorizonDays: 0,
  timeSliderBoundaries: null,
  timeSliderFlowDirection: null,
  timeSliderDate: '',
  snapshotPshWells: null,

  setSelectedHorizonDays: (days) => set({ selectedHorizonDays: days }),
  setTimeSliderBoundaries: (boundaries) => set({ timeSliderBoundaries: boundaries }),
  setTimeSliderFlowDirection: (direction) => set({ timeSliderFlowDirection: direction }),
  setTimeSliderDate: (date) => set({ timeSliderDate: date }),
  setSnapshotPshWells: (wells) => set({ snapshotPshWells: wells }),

  resetForSite: (siteId) => {
    if (get().siteId === siteId) return;
    set({
      siteId,
      selectedHorizonDays: 0,
      timeSliderBoundaries: null,
      timeSliderFlowDirection: null,
      timeSliderDate: '',
      snapshotPshWells: null,
    });
  },
}));
